import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../utils/i18n';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Globe } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [selectedLocale, setSelectedLocale] = useState('de');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [branding, setBranding] = useState({ logo: null });
  const { login, signup, locale } = useAuth();
  const { t } = useTranslation(locale);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/admin/branding/public`);
        setBranding(response.data);
      } catch (error) {
        console.error('Failed to fetch branding:', error);
      }
    };
    fetchBranding();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    const result = await login(email, password);
    
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    const result = await signup(email, password, selectedLocale);
    
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#0B0F14', position: 'relative', overflow: 'hidden' }}>
      {/* Logo als Hintergrund-Wasserzeichen */}
      {branding.logo && (
        <div 
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '80%',
            maxWidth: '800px',
            opacity: 0.03,
            zIndex: 0,
            pointerEvents: 'none'
          }}
        >
          <img 
            src={branding.logo} 
            alt="Background Logo" 
            style={{ 
              width: '100%', 
              height: 'auto',
              filter: 'blur(2px)'
            }}
          />
        </div>
      )}
      
      <Card className="w-full max-w-md" style={{ backgroundColor: '#0F141A', borderColor: '#1F2937', position: 'relative', zIndex: 1 }}>
        <CardHeader className="text-center">
          {/* Logo im Header */}
          {branding.logo && (
            <div className="flex justify-center mb-4">
              <img 
                src={branding.logo} 
                alt="Carcortex-ai Logo" 
                style={{ height: '60px', width: 'auto' }}
              />
            </div>
          )}
          <CardTitle className="text-2xl" style={{ color: '#E6EDF3', fontFamily: 'Manrope' }}>
            {branding.logo ? '' : 'Automotive Diagnostic'}
          </CardTitle>
          <CardDescription style={{ color: '#94A3B8' }}>
            KI-gestützte Fahrzeugdiagnose
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="login" data-testid="login-tab">{t('login')}</TabsTrigger>
              <TabsTrigger value="signup" data-testid="signup-tab">{t('signup')}</TabsTrigger>
            </TabsList>
            
            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="email" style={{ color: '#E6EDF3' }}>{t('email')}</Label>
                  <Input
                    id="email"
                    data-testid="login-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="mt-1"
                    style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                  />
                </div>
                <div>
                  <Label htmlFor="password" style={{ color: '#E6EDF3' }}>{t('password')}</Label>
                  <Input
                    id="password"
                    data-testid="login-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="mt-1"
                    style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                  />
                </div>
                {error && (
                  <div className="text-sm" style={{ color: '#EF4444' }} data-testid="error-message">
                    {error}
                  </div>
                )}
                <Button
                  type="submit"
                  data-testid="login-submit-button"
                  disabled={loading}
                  className="w-full"
                  style={{ backgroundColor: '#1EA0E6', color: '#ffffff' }}
                >
                  {loading ? t('loading') : t('login')}
                </Button>
              </form>
            </TabsContent>
            
            <TabsContent value="signup">
              <div className="text-center mb-4 p-4 rounded-lg" style={{ backgroundColor: '#0F141A', borderColor: '#1F2937', border: '1px solid' }}>
                <p className="text-sm mb-3" style={{ color: '#94A3B8' }}>
                  Für den vollständigen Registrierungsprozess mit Plan-Auswahl:
                </p>
                <Button
                  onClick={() => navigate('/signup')}
                  className="w-full"
                  style={{ backgroundColor: '#1EA0E6', color: '#ffffff' }}
                >
                  Zur vollständigen Registrierung →
                </Button>
              </div>
              
              <div className="text-center my-4" style={{ color: '#94A3B8' }}>
                oder schnelle Registrierung:
              </div>
              
              <form onSubmit={handleSignup} className="space-y-4">
                <div>
                  <Label htmlFor="signup-email" style={{ color: '#E6EDF3' }}>{t('email')}</Label>
                  <Input
                    id="signup-email"
                    data-testid="signup-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="mt-1"
                    style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                  />
                </div>
                <div>
                  <Label htmlFor="signup-password" style={{ color: '#E6EDF3' }}>{t('password')}</Label>
                  <Input
                    id="signup-password"
                    data-testid="signup-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="mt-1"
                    style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                  />
                </div>
                <div>
                  <Label style={{ color: '#E6EDF3' }}>
                    <Globe className="inline w-4 h-4 mr-2" />
                    Sprache / Language
                  </Label>
                  <Select value={selectedLocale} onValueChange={setSelectedLocale}>
                    <SelectTrigger data-testid="locale-select" className="mt-1" style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="de">Deutsch</SelectItem>
                      <SelectItem value="en">English</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {error && (
                  <div className="text-sm" style={{ color: '#EF4444' }} data-testid="error-message">
                    {error}
                  </div>
                )}
                <Button
                  type="submit"
                  data-testid="signup-submit-button"
                  disabled={loading}
                  className="w-full"
                  style={{ backgroundColor: '#1EA0E6', color: '#ffffff' }}
                >
                  {loading ? t('loading') : t('signup')}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};
